<template>
  <section class="comm-page dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>
    <section class="dp-right">
      <div class="comm-page-head">
        <h2>{{ departmentName ? `${departmentName} · 通话配置` : '通话配置' }}</h2>
        <p v-if="departmentId > 0" class="comm-page-desc">管理本校通话限制策略，保存后立即对终端生效。</p>
      </div>

      <div v-if="departmentId > 0" class="config-stack">
        <article class="config-card">
          <header class="config-card__head">
            <div>
              <h3 class="config-card__title">通话时长限制</h3>
              <p class="config-card__desc">限制单次通话的最大时长，超时自动挂断；填 0 表示不限制。</p>
            </div>
            <el-tag size="mini" type="info" effect="plain">学校级</el-tag>
          </header>

          <div class="config-card__body">
            <div class="config-row">
              <div class="config-row__meta">
                <div class="config-row__label">单次最大时长</div>
                <div class="config-row__hint">单位：分钟</div>
              </div>
              <div class="config-row__control">
                <el-input-number
                  v-model="form.MaxCallDuration"
                  :min="0"
                  :max="999"
                  controls-position="right"
                />
                <span class="config-row__unit">分钟</span>
                <span class="config-row__badge" :class="{ 'is-unlimited': isUnlimited }">
                  {{ isUnlimited ? '不限制' : `${form.MaxCallDuration} 分钟` }}
                </span>
              </div>
            </div>
          </div>

          <footer class="config-card__foot">
            <el-button type="primary" :loading="loading" @click="handleSave">保存配置</el-button>
          </footer>
        </article>
      </div>

      <div v-else class="comm-empty-tip">请选择学校后进行通话配置</div>
    </section>
  </section>
</template>

<script>
import './styles/comm-page.css'
import DepartmentPane from '../../components/DepartmentPane'
import { getCommCallConfig, saveCommCallConfig } from '../../api/modules/misc'

/**
 * 学校通话配置（单次最大时长等）。最后修改：2026-08-07
 */
export default {
  name: 'CallConfig',
  components: { DepartmentPane },
  data() {
    return {
      departmentId: 0,
      departmentName: '',
      loading: false,
      form: {
        DepartmentId: 0,
        MaxCallDuration: 0
      }
    }
  },
  computed: {
    isUnlimited() {
      return !this.form.MaxCallDuration || Number(this.form.MaxCallDuration) === 0
    }
  },
  methods: {
    treeNodeClick(data) {
      this.departmentId = data && data.value ? data.value : 0
      this.departmentName = data && data.label ? data.label : ''
      this.loadConfig()
    },
    async loadConfig() {
      if (!this.departmentId) return
      this.loading = true
      try {
        const res = await getCommCallConfig({ departmentId: this.departmentId })
        if (res.data.success) {
          this.form = Object.assign({}, res.data.response || {}, { DepartmentId: this.departmentId })
        } else {
          this.$message.error(res.data.msg || '获取配置失败')
        }
      } finally {
        this.loading = false
      }
    },
    async handleSave() {
      if (!this.departmentId) {
        this.$message.warning('请先选择学校')
        return
      }
      this.loading = true
      try {
        const payload = Object.assign({}, this.form, { DepartmentId: this.departmentId })
        const res = await saveCommCallConfig(payload)
        if (res.data.success) {
          this.$message.success(res.data.msg || '保存成功')
          this.loadConfig()
        } else {
          this.$message.error(res.data.msg || '保存失败')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.comm-page-desc {
  margin: -2px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.config-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  align-self: flex-start;
  width: 100%;
}

.config-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.config-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.config-card__title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.config-card__desc {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.config-card__body {
  padding: 4px 8px;
}

.config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 10px;
}

.config-row__meta {
  min-width: 0;
}

.config-row__label {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.3;
}

.config-row__hint {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.config-row__control {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.config-row__unit {
  font-size: 13px;
  color: #64748b;
}

.config-row__badge {
  display: inline-flex;
  align-items: center;
  min-width: 64px;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

.config-row__badge.is-unlimited {
  background: #f1f5f9;
  color: #64748b;
}

.config-card__foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.config-card__foot .el-button {
  min-width: 96px;
}
</style>
